import React, { useEffect, useRef, useState } from "react";
import styles from './CaseLog.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { Accordion, AccordionItem } from "../../../components/accordion/Accordion";
import PreCaseForm from "./preCaseForm/PreCaseForm";
import CaseStartForm from "./caseStartForm/CaseStartForm";
import AirwayAnesthesiaForm from "./airwayAnesthesiaForm/AirwayAnesthesiaForm";
import MonitoringProceduresForm from "./monitoringProceduresForm/MonitoringProceduresForm";
import AdditionalProceduresForm from "./additionalProceduresForm/AdditionalProceduresForm";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import Button from "../../../components/button/Button";
import Modal from "../../../components/modal/Modal";

const CaseLog = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [clinicalSitesList, setClinicalSitesList] = useState([]);
  const [instructorList, setInstructorList] = useState([]);
  const [studentYearList, setStudentYearList] = useState([]);
  const [specialitiesList, setSpecialitiesList] = useState([]);
  const [surgicalServiceList, setSurgicalServiceList] = useState([]);
  const [patientPhysicalStatusList, setPatientPhysicalStatusList] = useState([]);
  const [patientAgeList, setPatientAgeList] = useState([]);
  const [ageRangesList, setAgeRangesList] = useState([]);
  const [patientPositionsList, setPatientPositionsList] = useState([]);
  const [anatomicLocationOfProceduresList, setAnatomicLocationOfProceduresList] = useState([]);
  const [maskManagementList, setMaskManagementList] = useState([]);
  const [typeOfAnestheticList, setTypeOfAnestheticList] = useState([]);
  const [supraGlotticAirwayList, setSupraGlotticAirwayList] = useState([]);
  const [trachealIntubationList, setTrachealIntubationList] = useState([]);
  const [regionalAnesthesiaAdministrationList, setRegionalAnesthesiaAdministrationList] = useState([]);
  const [numberOfBlocksTotalOnCaseList, setNumberOfBlocksTotalOnCaseList] = useState([]);
  const [peripheralIVPlacedList, setPeripheralIVPlacedList] = useState([]);
  const [totalNumberOfIVsPlacedList, setTotalNumberOfIVsPlacedList] = useState([]);
  const [preCaseFormData, setPreCaseFormData] = useState({
    clinicalSiteId: null,
    dateOfCase: '',
    instructorId: null,
    yearId: null,
    specialityIds: [],
    surgicalService: [],
    patientPhysicalStatus: null,
    emergency: null,
    patientAge: null,
    ageRange: null,
    surgeryDescription: '',
  });
  const [preCaseFormErrorsCount, setPreCaseFormErrorsCount] = useState(0);
  const [caseStartFormData, setCaseStartFormData] = useState({
    isSimulatedClinicalExperience: null,
    caseCount: null,
    startTime: '',
    endTime: '',
    patientWeight: null,
    patientPosition: [],
    anatomicLocationOfProcedure: [],
    maskManagement: null,
    typeOfAnesthetic: null,
    supraGlotticAirwayDevice: null,
    inhalationInduction: null,
    ventilationByMask: null
  });
  const [caseStartFormErrorsCount, setCaseStartErrorsCount] = useState(0);
  const [airwayAnesthesiaFormData, setAirwayAnesthesiaFormData] = useState({
    trachealIntubation: null,
    regionalAnesthesiaAdministration: null,
    numberOfBlocksTotalOnCase: null,
    regionalManagementOnly: false,
    regionalSimulationExperience: false,
    presentForEmergence: null
  });
  const [airwayAnesthesiaFormErrorsCount, setAirwayAnesthesiaFormErrorsCount] = useState(0);
  const [monitoringProceduresFormData, setMonitoringProceduresFormData] = useState({
    intraArterialBPMonitoring: null,
    arterialPunctureCatheterInsertion: null,
    centralVenousPressureMonitoring: null,
    centralVenousCatheterPlacement: null,
    pulmonaryPressureMonitoring: null,
    pulmonaryArterialCatheterPlacement: null,
    peripheralIVPlaced: null,
    peripheralIVValue: null,
    totalIVPlaced: null,
  })
  const [monitoringProceduresFormErrorsCount, setMonitoringProceduresFormErrorsCount] = useState(0);
  const [additionalProceduresFormData, setAdditionalProceduresFormData] = useState({
    ogNGTubePlaced: null,
    wasUltrasoundUsed: null,
    numberOfProceduresTheUltrasoundWasUsedFor: null,
    advancedNonInvasiveMonitoring: null,
    assessmentOfChestXRay: null,
    postoperativeAssessmentOrExam: null,
    preoperativeAssessmentOrExamIncludingHorP: null,
  });
  const [additionalProceduresFormErrorsCount, setAdditionalProceduresFormErrorsCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [status, setStatus] = useState(null);
  const [activeCaseCount, setActiveCaseCount] = useState(1);
  const [student, setStudent] = useState({});
  const { logReportId, caseLogId, studentId } = useParams();
  const { showToast } = useToasts();
  const preCaseFormRef = useRef();
  const caseStartFormRef = useRef();
  const airwayAnesthesiaFormRef = useRef();
  const monitoringProceduresFormRef = useRef();
  const additionalProceduresFormRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`case-log/options`, {
        params: studentId ? {
          studentId
        } : {}
      })
      .then((response) => {
        if(response.data?.clinicalSites) {
          setClinicalSitesList(response.data.clinicalSites.map((clinicalSite) => ({
            value: clinicalSite.id,
            label: clinicalSite.name,
          })));
        }
        if(response.data?.instructors) {
          setInstructorList(response.data.instructors.map((instructor) => ({
            value: instructor.id,
            label: `${instructor.firstName} ${instructor.lastName}`,
          })));
        }
        if(response.data?.studentYear) {
          setStudentYearList([{
            value: response.data.studentYear.id,
            label: response.data.studentYear.year,
          }]);
        }
        if(response.data?.specialities) {
          setSpecialitiesList(response.data.specialities.map((speciality) => ({
            value: speciality.id,
            label: speciality.name,
          })));
        }
        if(response.data?.surgicalService) {
          setSurgicalServiceList(response.data.surgicalService.map((surgicalService) => ({
            value: surgicalService,
            label: surgicalService,
          })));
        }
        if(response.data?.patientPhysicalStatus) {
          setPatientPhysicalStatusList(response.data.patientPhysicalStatus.map((patientPhysicalStatus) => ({
            value: patientPhysicalStatus,
            label: patientPhysicalStatus,
          })));
        }
        if(response.data?.patientAge) {
          setPatientAgeList(response.data.patientAge.map((patientAge) => ({
            value: patientAge.name,
            label: patientAge.name,
            subOptions: patientAge.months ? patientAge.months.map((month) => ({
              value: {
                year: patientAge.name,
                month: month
              },
              label: month,
            })) : []
          })))
        }
        if(response.data?.ageRanges) {
          setAgeRangesList(response.data.ageRanges.map((ageRange) => ({
            value: ageRange,
            label: ageRange,
          })));
        }
        if(response.data?.patientPositions) {
          setPatientPositionsList(response.data.patientPositions.map((patientPositions) => ({
            value: patientPositions,
            label: patientPositions,
          })));
        }
        if(response.data?.anatomicLocationOfProcedures) {
          setAnatomicLocationOfProceduresList(response.data.anatomicLocationOfProcedures.map((anatomicLocationOfProcedure) => ({
            value: anatomicLocationOfProcedure.name,
            label: anatomicLocationOfProcedure.name,
            subOptions: anatomicLocationOfProcedure.options ? anatomicLocationOfProcedure.options.map((option) => ({
              value: `${anatomicLocationOfProcedure.name} - ${option}`,
              label: option,
            })) : []
          })))
        }
        if(response.data?.maskManagement) {
          setMaskManagementList(response.data.maskManagement.map((maskManagement) => ({
            value: maskManagement,
            label: maskManagement,
          })));
        }
        if(response.data?.typeOfAnesthetic) {
          setTypeOfAnestheticList(response.data.typeOfAnesthetic.map((typeOfAnesthetic) => ({
            value: typeOfAnesthetic,
            label: typeOfAnesthetic,
          })));
        }
        if(response.data?.supraGlotticAirway) {
          setSupraGlotticAirwayList(response.data.supraGlotticAirway.map((supraGlotticAirway) => ({
            value: supraGlotticAirway,
            label: supraGlotticAirway,
          })));
        }
        if(response.data?.trachealIntubationOptions) {
          setTrachealIntubationList(response.data.trachealIntubationOptions.map((trachealIntubation) => ({
            value: trachealIntubation.name,
            label: trachealIntubation.name,
            subOptions: trachealIntubation.options ? trachealIntubation.options.map((option) => ({
              value: `${trachealIntubation.name} - ${option}`,
              label: option,
            })) : []
          })))
        }
        if(response.data?.regionalAnesthesiaAdministration) {
          setRegionalAnesthesiaAdministrationList(response.data.regionalAnesthesiaAdministration.map((regionalAnesthesiaAdministration) => ({
            value: regionalAnesthesiaAdministration.name,
            label: regionalAnesthesiaAdministration.name,
            subOptions: regionalAnesthesiaAdministration.options ? regionalAnesthesiaAdministration.options.flatMap((option) => {
              if(option?.options) {
                return option.options.map((subOption) => ({
                  value: `${regionalAnesthesiaAdministration.name} ${option.name} - ${subOption.name}`,
                  label: `${option.name} - ${subOption.name}`,
                }));
              } else {
                return {
                  value: `${regionalAnesthesiaAdministration.name} - ${option.name}`,
                  label: option.name,
                }
              }
            }) : []
          })))
        }
        if(response.data?.numberOfBlocksTotalOnCase) {
          setNumberOfBlocksTotalOnCaseList(response.data.numberOfBlocksTotalOnCase.map((numberOfBlocksTotalOnCase) => ({
            value: numberOfBlocksTotalOnCase,
            label: numberOfBlocksTotalOnCase,
          })));
        }
        if(response.data?.peripheralIVPlaced) {
          setPeripheralIVPlacedList(response.data.peripheralIVPlaced.map((peripheralIVPlaced) => ({
            value: peripheralIVPlaced,
            label: peripheralIVPlaced,
          })));
        }
        if(response.data?.totalNumberOfIVsPlaced) {
          setTotalNumberOfIVsPlacedList(response.data.totalNumberOfIVsPlaced.map((totalNumberOfIVsPlaced) => ({
            value: totalNumberOfIVsPlaced,
            label: totalNumberOfIVsPlaced,
          })));
        }
      })
    };

    fetchData();
  }, [studentId]);
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`case-log/${caseLogId}`)
      .then((response) => {
        if(response.data?.caseLog?.student){
          setStudent(response.data.caseLog.student)
        }
        setPreCaseFormData((prevData) => ({
          ...prevData,
          clinicalSiteId: response.data?.caseLog?.clinicalSite?.id,
          dateOfCase: response.data?.caseLog?.dateOfCase,
          instructorId: response.data?.caseLog?.instructor?.id,
          yearId: response.data?.caseLog?.studentYear?.id,
          specialityIds: response.data?.caseLog?.specialities ? 
            response.data?.caseLog?.specialities.map((speciality) => speciality.id) : 
            [],
          surgicalService: response.data?.caseLog?.surgicalService ?? [],
          patientPhysicalStatus: response.data?.caseLog?.patientPhysicalStatus,
          emergency: response.data?.caseLog?.emergency,
          patientAge: response.data?.caseLog?.patientAgeMonth ? {
            year: response.data?.caseLog?.patientAge,
            month: response.data?.caseLog?.patientAgeMonth
          } : response.data?.caseLog?.patientAge,
          ageRange: response.data?.caseLog?.ageRange,
          surgeryDescription: response.data?.caseLog?.surgeryDescription
        }));
        setCaseStartFormData((prevData) => ({
          ...prevData,
          isSimulatedClinicalExperience: response.data?.caseLog?.isSimulatedClinicalExperience,
          caseCount: response.data?.caseLog?.caseCount,
          startTime: response.data?.caseLog?.startTime,
          endTime: response.data?.caseLog?.endTime,
          patientWeight: response.data?.caseLog?.patientWeight,
          patientPosition: response.data?.caseLog?.patientPosition ?? [],
          anatomicLocationOfProcedure: response.data?.caseLog?.anatomicLocationOfProcedure ?? [],
          maskManagement: response.data?.caseLog?.maskManagement,
          typeOfAnesthetic: response.data?.caseLog?.typeOfAnesthetic,
          supraGlotticAirwayDevice: response.data?.caseLog?.supraGlotticAirwayDevice,
          inhalationInduction: response.data?.caseLog?.inhalationInduction,
          ventilationByMask: response.data?.caseLog?.ventilationByMask
        }))
        setAirwayAnesthesiaFormData((prevData) => ({
          ...prevData,
          trachealIntubation: response.data?.caseLog?.trachealIntubation,
          regionalAnesthesiaAdministration: response.data?.caseLog?.regionalAnesthesiaAdministration,
          numberOfBlocksTotalOnCase: response.data?.caseLog?.numberOfBlocksTotalOnCase,
          regionalManagementOnly: response.data?.caseLog?.regionalManagementOnly,
          regionalSimulationExperience: response.data?.caseLog?.regionalSimulationExperience,
          presentForEmergence: response.data?.caseLog?.presentForEmergence
        }));
        setMonitoringProceduresFormData((prevData) => ({
          ...prevData,
          intraArterialBPMonitoring: response.data?.caseLog?.intraArterialBPMonitoring,
          arterialPunctureCatheterInsertion: response.data?.caseLog?.arterialPunctureCatheterInsertion,
          centralVenousPressureMonitoring: response.data?.caseLog?.centralVenousPressureMonitoring,
          centralVenousCatheterPlacement: response.data?.caseLog?.centralVenousCatheterPlacement,
          pulmonaryPressureMonitoring: response.data?.caseLog?.pulmonaryPressureMonitoring,
          pulmonaryArterialCatheterPlacement: response.data?.caseLog?.pulmonaryArterialCatheterPlacement,
          peripheralIVPlaced: response.data?.caseLog?.peripheralIVPlaced,
          peripheralIVValue: response.data?.caseLog?.peripheralIVValue,
          totalIVPlaced: response.data?.caseLog?.totalIVPlaced,
        }));
        setAdditionalProceduresFormData((prevData) => ({
          ...prevData,
          ogNGTubePlaced: response.data?.caseLog?.ogNGTubePlaced,
          wasUltrasoundUsed: response.data?.caseLog?.wasUltrasoundUsed,
          numberOfProceduresTheUltrasoundWasUsedFor: response.data?.caseLog?.numberOfProceduresTheUltrasoundWasUsedFor,
          advancedNonInvasiveMonitoring: response.data?.caseLog?.advancedNonInvasiveMonitoring,
          assessmentOfChestXRay: response.data?.caseLog?.assessmentOfChestXRay,
          postoperativeAssessmentOrExam: response.data?.caseLog?.postoperativeAssessmentOrExam,
          preoperativeAssessmentOrExamIncludingHorP: response.data?.caseLog?.preoperativeAssessmentOrExamIncludingHorP,
        }));
        setStatus(response.data?.caseLog?.status)
        setActiveCaseCount(response.data?.activeCaseCount ?? 1)
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    }

    fetchData();
  }, [caseLogId, showToast]);

  const handleSave = async (data) => {
    await axiosClient.patch(`case-log/${caseLogId}`, data)
    .catch((error) => {
      showToast(error.message, 'error');
    });
  };

  const validatePreCaseForm = () => {
    const errorsCount = preCaseFormRef.current.validateForm();
    if(errorsCount) {
      setPreCaseFormErrorsCount(errorsCount);
      return false;
    } else {
      setPreCaseFormErrorsCount(0);
      return true;
    }
  }

  const validateCaseStartForm = () => {
    const errorsCount = caseStartFormRef.current.validateForm();
    if(errorsCount) {
      setCaseStartErrorsCount(errorsCount);
      return false;
    } else {
      setCaseStartErrorsCount(0);
      return true;
    }
  }

  const validateAirwayAnesthesiaForm = () => {
    const errorsCount = airwayAnesthesiaFormRef.current.validateForm();
    if(errorsCount) {
      setAirwayAnesthesiaFormErrorsCount(errorsCount);
      return false;
    } else {
      setAirwayAnesthesiaFormErrorsCount(0);
      return true;
    }
  }

  const validateMonitoringProceduresForm = () => {
    const errorsCount = monitoringProceduresFormRef.current.validateForm();
    if(errorsCount) {
      setMonitoringProceduresFormErrorsCount(errorsCount);
      return false;
    } else {
      setMonitoringProceduresFormErrorsCount(0);
      return true;
    }
  }

  const validateAdditionalProceduresForm = () => {
    const errorsCount = additionalProceduresFormRef.current.validateForm();
    if(errorsCount) {
      setAdditionalProceduresFormErrorsCount(errorsCount);
      return false;
    } else {
      setAdditionalProceduresFormErrorsCount(0);
      return true;
    }
  }

  const handleValidate = () => {
    setIsValidated(true);
    const isPreCaseValid = validatePreCaseForm();
    const isCaseStartValid = validateCaseStartForm();
    const isAirwayAnesthesiaValid = validateAirwayAnesthesiaForm();
    const isMonitoringProceduresValid = validateMonitoringProceduresForm();
    const isAdditionalProceduresValid = validateAdditionalProceduresForm();

    if (isPreCaseValid && isCaseStartValid && isAirwayAnesthesiaValid &&
        isMonitoringProceduresValid && isAdditionalProceduresValid) {
      setShowModal(true);
    }
  }

  useEffect(() => {
    if(isValidated) {
      validatePreCaseForm();
    }
  }, [preCaseFormData, isValidated]);

  useEffect(() => {
    if(isValidated) {
      validateCaseStartForm();
    }
  }, [caseStartFormData, isValidated]);

  useEffect(() => {
    if(isValidated) {
      validateAirwayAnesthesiaForm();
    }
  }, [airwayAnesthesiaFormData, isValidated]);

  useEffect(() => {
    if(isValidated) {
      validateMonitoringProceduresForm();
    }
  }, [monitoringProceduresFormData, isValidated]);

  useEffect(() => {
    if(isValidated) {
      validateAdditionalProceduresForm();
    }
  }, [additionalProceduresFormData, isValidated]);

  const handleSubmit = async (createNewCaseLog) => {
    setShowModal(false);
    setIsLoading(true);
    await axiosClient.patch(`log-reports/${logReportId}/submit`, {
      createNewCaseLog: createNewCaseLog,
      caseLogId: parseInt(caseLogId)
    })
    .then((response) => {
      showToast(response.message);
      setIsValidated(false);
      if(response.data?.caseLog) {
        navigate(`/log-reports/${logReportId}/case-logs/${response.data.caseLog.id}`);
      } else {
        navigate(`/log-reports`);
      }
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={studentId ? [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `${student.firstName ?? ''} ${student.lastName ?? ''}`, path: `/log-reports/student/${studentId}` }, 
            { label: `Log Report - ${logReportId}`, path: `/log-reports/student/${studentId}/log-reports/${logReportId}/case-logs` },
            { label: `Case Log - ${caseLogId}` }
          ] : [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `Log Report - ${logReportId}`, path: `/log-reports/${logReportId}/case-logs` },
            { label: `Case Log - ${caseLogId}` }
          ]}
        />
        {status !== 'Completed' && !isLoading && <Button
          label="Submit Case"
          size="medium"
          onClick={handleValidate}
        />}
      </div>
      <div className={styles.container}>
        <Accordion key={caseLogId}>
          <AccordionItem 
            title={
              <span className={styles.accordioTitle}>
                Pre-Case Information
                {preCaseFormErrorsCount > 0 && <span className={styles.count}>{preCaseFormErrorsCount}</span>}
              </span>
            }
          >
            <PreCaseForm
              formData={preCaseFormData}
              clinicalSitesList={clinicalSitesList}
              instructorList={instructorList}
              studentYearList={studentYearList}
              specialitiesList={specialitiesList}
              surgicalServiceList={surgicalServiceList}
              patientPhysicalStatusList={patientPhysicalStatusList}
              patientAgeList={patientAgeList}
              ageRangesList={ageRangesList}
              onSave={(updatedData) => {
                handleSave(updatedData);
                if(updatedData.patientAgeMonth) {
                  updatedData.patientAge = {
                    year: updatedData.patientAge,
                    month: updatedData.patientAgeMonth
                  }
                }
                setPreCaseFormData((prevData) => ({
                  ...prevData,
                  ...updatedData
                }));
              }}
              ref={preCaseFormRef}
              disabled={status === 'Completed'}
            />
          </AccordionItem>
          <AccordionItem 
            title={
              <span className={styles.accordioTitle}>
                Case Start Information
                {caseStartFormErrorsCount > 0 && <span className={styles.count}>{caseStartFormErrorsCount}</span>}
              </span>
            }
          >
            <CaseStartForm
              formData={caseStartFormData}
              patientPositionsList={patientPositionsList}
              anatomicLocationOfProceduresList={anatomicLocationOfProceduresList}
              maskManagementList={maskManagementList}
              typeOfAnestheticList={typeOfAnestheticList}
              supraGlotticAirwayList={supraGlotticAirwayList}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setCaseStartFormData((prevData) => ({
                  ...prevData,
                  ...updatedData
                }))
              }}
              ref={caseStartFormRef}
              disabled={status === 'Completed'}
            />
          </AccordionItem>
          <AccordionItem 
            title={
              <span className={styles.accordioTitle}>
                Airway and Anesthesia Management
                {airwayAnesthesiaFormErrorsCount > 0 && <span className={styles.count}>{airwayAnesthesiaFormErrorsCount}</span>}
              </span>
            }
          >
            <AirwayAnesthesiaForm
              formData={airwayAnesthesiaFormData}
              trachealIntubationList={trachealIntubationList}
              regionalAnesthesiaAdministrationList={regionalAnesthesiaAdministrationList}
              numberOfBlocksTotalOnCaseList={numberOfBlocksTotalOnCaseList}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setAirwayAnesthesiaFormData((prevData) => ({
                  ...prevData,
                  ...updatedData
                }))
              }}
              ref={airwayAnesthesiaFormRef}
              disabled={status === 'Completed'}
            />
          </AccordionItem>
          <AccordionItem 
            title={
              <span className={styles.accordioTitle}>
                Monitoring and Procedures
                {monitoringProceduresFormErrorsCount > 0 && <span className={styles.count}>{monitoringProceduresFormErrorsCount}</span>}
              </span>
            }
          >
            <MonitoringProceduresForm
              formData={monitoringProceduresFormData}
              peripheralIVPlacedList={peripheralIVPlacedList}
              totalNumberOfIVsPlacedList={totalNumberOfIVsPlacedList}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setMonitoringProceduresFormData((prevData) => ({
                  ...prevData,
                  ...updatedData
                }))
              }}
              ref={monitoringProceduresFormRef}
              disabled={status === 'Completed'}
            />
          </AccordionItem>
          <AccordionItem 
            title={
              <span className={styles.accordioTitle}>
                Additional Procedures and End of Case
                {additionalProceduresFormErrorsCount > 0 && <span className={styles.count}>{additionalProceduresFormErrorsCount}</span>}
              </span>
            }
          >
            <AdditionalProceduresForm
              formData={additionalProceduresFormData}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setAdditionalProceduresFormData((prevData) => ({
                  ...prevData,
                  ...updatedData
                }))
              }}
              ref={additionalProceduresFormRef}
              disabled={status === 'Completed'}
            />
          </AccordionItem>
        </Accordion>
      </div>
      {showModal && <Modal>
        <div className={styles.submitModal}>
          <p className={styles.activeCaseText}>Active Cases: <span>{activeCaseCount}</span></p>
          <h2 className={styles.modalTitle}>Would you like to add another case for the day?</h2>
          <div className={styles.buttons}>
            <Button
              label="No! Submit the Log Report"
              size="small"
              variant="white"
              onClick={() => handleSubmit(false)}
            />
            <Button
              label="Yes & Add New Case"
              size="small"
              onClick={() => handleSubmit(true)}
            />
          </div>
        </div>
      </Modal>}
    </>
  );
};

export default CaseLog;